import React, { useState, useEffect, useCallback } from 'react';
import ManualUpdateForm from './ManualUpdateForm';
import companyAPI from '../../services/companyAPI';
import BulkUpdateTool from './BulkUpdateTool';
import ContainerSearch from './ContainerSearch';
import UpdateHistory from './UpdateHistory';
import DashboardStats from './DashboardStats';
import RecentContainersGrid from './RecentContainersGrid';
import ContainerForm from './ContainerForm'; 
import ContainerReport from './ContainerReport';
import containerAPI from '../../services/containerAPI';
import LoadingSpinner from '../common/LoadingSpinner';
import './AgentDashboard.css';
import { decodeJWT } from '../../utils/jwt';
import CustomerPortal from '../customer/CustomerPortal';
import BulkContainerUpdate from './BulkContainerUpdate';

const STATUS_CATEGORY_MAP = {
  'Container Return (Empty)': 'Completed',
  'Consignee Gate-Out (Pick Up)': 'Delivered',
  'Delivery Order (D.O.) Collected': 'Ready for Pickup',
  'Delivery Order (D.O.) Issued': 'Ready for Pickup',
  'Container Discharged': 'Discharged',
  'Vessel Sailed (Departure)': 'IN Transit',
  'Loaded on Vessel': 'Loaded',
  'Export Gate-In': 'Gated IN',
  'Shipper Pick': 'Shipper Pick Up' // Note: This should match your milestone
};

// Helper to get business status from backend statusDesc
function getBusinessStatus(statusDesc) {
  return STATUS_CATEGORY_MAP[statusDesc] || statusDesc;
}

const AgentDashboard = () => {
  const [activeTab, setActiveTab] = useState(() => sessionStorage.getItem('agentActiveTab') || 'dashboard');
  const [companyName, setCompanyName] = useState('');
  const [stats, setStats] = useState(null);
  const [recentContainers, setRecentContainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [statusMappings, setStatusMappings] = useState({});

 
  const handleRefresh = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []); 

  useEffect(() => {
    const token = sessionStorage.getItem('agentToken');
    const agent = decodeJWT(token);
    loadDashboardData(agent);
    //loadStatusMappings(); // Ensure this is stable if uncommented
    if (agent && agent.companyId) {
      companyAPI.getCompanyDetails()
        .then(res => setCompanyName(res.data.companyName || ''))
        .catch(() => setCompanyName(''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]); // Correctly depends on refreshTrigger

  const loadDashboardData = async (agent) => {
    try {
      setLoading(true);
      const companyId = agent && agent.companyId ? agent.companyId : 1;
      const [statsResponse, containersResponse] = await Promise.all([
        containerAPI.getStats(companyId),
        containerAPI.getAll(companyId)
      ]); 
      setStats(statsResponse.data);
      // Get recent containers (last 20, sorted by most recent update)
      const sortedContainers = containersResponse.data
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
        .slice(0, 20);
      setRecentContainers(sortedContainers);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleContainerCreated = () => {
    alert('Container created successfully! You can now track its status.');
    handleRefresh(); // Calls the stable function
    setActiveTab('dashboard'); 
  };

  // Updated tabs with Container Creation
  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'create-container', label: 'Quick Booking', icon: '➕' }, 
    { id: 'bulk-container-update', label: 'Bulk Bookings', icon: '🗂️' },
    { id: 'quick-update', label: 'Quick Status Update', icon: '⚡' },
    { id: 'bulk-update', label: 'Bulk Status Update', icon: '📦' },
    { id: 'track-container', label: 'Track Container', icon: '🚚' },
    { id: 'recent-containers', label: 'Recent Containers', icon: '📋' },
    { id: 'search', label: 'Search Containers', icon: '🔍' },
    { id: 'history', label: 'My Updates', icon: '📝' },
    { id: 'report', label: 'Report', icon: '📑' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <div className="dashboard-tab">
            <DashboardStats 
              stats={stats} 
              recentContainers={recentContainers.slice(0, 8)}
              loading={loading}
            />
          </div>
        );
      case 'quick-update': {
        const token = sessionStorage.getItem('agentToken');
        const agent = decodeJWT(token);
        return <ManualUpdateForm agent={agent} onUpdate={handleRefresh} statusMappings={statusMappings} />;
      }
      case 'bulk-update': {
        const token = sessionStorage.getItem('agentToken');
        const agent = decodeJWT(token);
        // handleRefresh is now stable
        return <BulkUpdateTool agent={agent} onUpdate={handleRefresh} statusMappings={statusMappings} />;
      }
      case 'create-container': {
        const token = sessionStorage.getItem('agentToken');
        const agent = decodeJWT(token);
        return <ContainerForm agent={agent} onContainerCreated={handleContainerCreated} />;
      }
      case 'recent-containers':
        return <RecentContainersGrid containers={recentContainers.map(c => ({
          ...c,
          businessStatus: getBusinessStatus(c.statusDesc)
        }))} loading={loading} />;
      case 'search': {
        const token = sessionStorage.getItem('agentToken');
        const agent = decodeJWT(token);
        return <ContainerSearch agent={agent} statusCategoryMap={STATUS_CATEGORY_MAP} />;
      }
      case 'history': {
        const token = sessionStorage.getItem('agentToken');
        const agent = decodeJWT(token);
        return <UpdateHistory agent={agent} />;
      }
      case 'track-container':
        return <CustomerPortal />;
      case 'report':
        return <ContainerReport />;
      case 'bulk-container-update': {
        const token = sessionStorage.getItem('agentToken');
        const agent = decodeJWT(token);
        return <BulkContainerUpdate agent={agent} onUpdate={handleRefresh} />;
      }
      default:
        return null;
    }
  };

  if (loading && activeTab === 'dashboard') {
    return (
      <div className="agent-dashboard">
        <div className="dashboard-header">
          <h1>Agent Portal</h1>
          {companyName && (
            <h2 className="company-name">{companyName}</h2>
          )}
        </div> 
        <LoadingSpinner text="Loading dashboard..." />
      </div>
    );
  }

  return (
    <div className="agent-dashboard">
      <div className="dashboard-header">
        <h1>Agent Portal</h1>
        {companyName && (
          <h2 className="company-name">{companyName}</h2>
        )}
      </div>
      <div className="dashboard-nav">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`nav-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => {
              setActiveTab(tab.id);
              sessionStorage.setItem('agentActiveTab', tab.id);
            }}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>
      <div className="dashboard-content">
        {renderTabContent()}
      </div>
      {/* Styles omitted for brevity */}
    </div>
  );
};

export default AgentDashboard;